import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useMercenary } from './contexts/MercenaryContext';
import { useNavigate } from 'react-router-dom';
import BannedBanner from './components/Shared/BannedBanner';
import { collectBrowserSignals } from './utils/browserSignals';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const AuthInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setUser } = useAuth();
  const { setMercenary, mercenaryId, setMercenaryId } = useMercenary();
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const navigate = useNavigate();
  const initialFetchDone = useRef(false);

  // ── UTM capture: run once on first load, before auth init ──────────────────
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const utm = params.get('utm_source');
      if (!utm) return;

      const source = utm.trim().toLowerCase().slice(0, 64);

      // Persist for registration form even after the URL is cleaned up
      localStorage.setItem('njord_referral', source);

      // Only ping the backend once per browser session to avoid duplicate rows
      const sessionKey = `njord_utm_sent_${source}`;
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, '1');

      axios.post(`${API_BASE}/api/auth/track-utm`, { source }, { withCredentials: true }).catch(() => {
        // Non-critical, don't surface to user
      });
    } catch {
      // Ignore any errors in UTM capture
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    const isNotFound = (error: any): boolean =>
      axios.isAxiosError(error) && error.response?.status === 404;

    const fetchUserData = async () => {
      try {
        const userResponse = await axios.get(`${API_BASE}/api/auth/user`, { withCredentials: true });
        if (userResponse.data) {
          setUser(userResponse.data);

          // Collect browser signals once on auth init and send to backend
          try {
            const signals = collectBrowserSignals();
            if (signals.signalScore > 0 || signals.webdriverDetected) {
              axios.post(`${API_BASE}/api/anti-cheat/browser-signal`, signals, { withCredentials: true }).catch(() => {
                // Non-critical, don't let signal submission failure break auth
              });
            }
          } catch {
            // Signal collection failure is non-critical
          }

          if (userResponse.data.isDead && userResponse.data.deathBattleReport) {
            navigate(`/deathscreen/${userResponse.data.deathBattleReport}`);
            return; // Do NOT load the mercenary into context while dead
          }

          // Step 1: try the stored mercenaryId if available
          let mercenaryResponse;
          if (mercenaryId) {
            try {
              mercenaryResponse = await axios.get(`${API_BASE}/api/mercenary/${mercenaryId}`, { withCredentials: true });
            } catch (error) {
              if (!isNotFound(error)) {
                // Not a 404, server/network problem, don't assume no mercenary exists
                throw error;
              }
              // 404 means this specific ID is gone; fall through to /first
              console.warn('Stored mercenaryId not found, falling back to first mercenary.');
            }
          }

          // Step 2: if we still don't have a mercenary, fetch the first active one
          if (!mercenaryResponse) {
            try {
              mercenaryResponse = await axios.get(`${API_BASE}/api/mercenary/first`, { withCredentials: true });
              setMercenaryId(mercenaryResponse.data.id);
            } catch (error) {
              if (isNotFound(error)) {
                // Server confirmed this user has no mercenary — go home.
                // Welcome.tsx detects offseason and shows the right UI;
                // during an active season it shows the Create Mercenary button.
                navigate('/home');
                return;
              }
              // Any other failure (5xx, network, CORS), don't assume no mercenary
              throw error;
            }
          }

          if (mercenaryResponse?.data) {
            setMercenary(mercenaryResponse.data);
          }
        }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 403 && error.response?.data?.banned) {
          // Server explicitly told us this account is banned
          setIsBanned(true);
        } else if (axios.isAxiosError(error) && error.response?.status === 401) {
          // Not logged in, send to hub for login
          window.location.href = 'https://njordheim.com';
          return;
        } else {
          console.error('Failed to initialise session:', error);
          setInitError(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (isBanned) {
    return <BannedBanner />;
  }

  if (initError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#1a1a2e', color: '#fff', gap: '16px' }}>
        <p>Could not connect to the server. Please refresh the page to try again.</p>
        <button onClick={() => window.location.reload()} style={{ padding: '8px 20px', cursor: 'pointer' }}>
          Refresh
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthInitializer;
